import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { VonageClient } from "@vonage/client-sdk";
import {
  useFetchVoiceConfigQuery,
  useGenerateWebrtcTokenMutation,
  useCreateVoiceTaskMutation,
} from "features/communicationTasks/communicationTasksAPI";
import { useFetchMeQuery } from "features/auth/authAPI";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { PERMISSIONS } from "features/permissions/constants";

export const SDK_STATUSES = {
  DISCONNECTED: "disconnected",
  CONNECTING: "connecting",
  CONNECTED: "connected",
};

export const CALL_STATES = {
  IDLE: "idle",
  CALLING: "calling",
  ACTIVE: "active",
  INCOMING: "incoming",
  ENDED: "ended",
  FAILED: "failed",
};

const InternalDialerContext = createContext(null);

export function useInternalDialer() {
  const ctx = useContext(InternalDialerContext);
  if (!ctx) {
    throw new Error("useInternalDialer must be used within an InternalDialerProvider");
  }
  return ctx;
}

export function InternalDialerProvider({ children }) {
  const {
    data: { [PERMISSIONS.PHONE__CALL_DEBTOR]: isCallDebtorAuthorized },
  } = useAuthorizations();
  const { data: voiceConfig, isLoading: isConfigLoading } = useFetchVoiceConfigQuery(undefined, {
    skip: !isCallDebtorAuthorized,
  });
  const [generateWebrtcToken] = useGenerateWebrtcTokenMutation();
  const [createVoiceTask] = useCreateVoiceTaskMutation();
  const { data: currentUser } = useFetchMeQuery();

  const [sdkStatus, setSdkStatus] = useState(SDK_STATUSES.DISCONNECTED);
  const [callState, setCallState] = useState(CALL_STATES.IDLE);
  const [incomingCallId, setIncomingCallId] = useState(null);
  const [incomingCallerPhone, setIncomingCallerPhone] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState(null);
  const [isWidgetVisible, setIsWidgetVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isSnoozed, setIsSnoozed] = useState(false);

  const clientRef = useRef(null);
  const activeCallIdRef = useRef(null);
  const incomingCallIdRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const serverCallPromiseRef = useRef(null);
  const endedToIdleTimerRef = useRef(null);
  const isConnectingRef = useRef(false);
  const callWasAnsweredRef = useRef(false);
  // Latched after a connect() failure so the auto-connect effect
  // doesn't immediately retry and hammer the token endpoint. The user
  // must explicitly re-engage (snooze/resume or manual click) to clear
  // it -- this trades automatic recovery for protection against a
  // tight loop when the token endpoint is down or the SDK is broken.
  const hasConnectionFailedRef = useRef(false);

  const isEnabled = voiceConfig?.isEnabled ?? false;
  const fromPhoneNumber = voiceConfig?.fromPhoneNumber ?? "";

  const clearDurationInterval = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  const startDurationTracking = useCallback(() => {
    clearDurationInterval();
    callStartTimeRef.current = Date.now();
    setCallDuration(0);
    durationIntervalRef.current = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - callStartTimeRef.current) / 1000));
    }, 1000);
  }, [clearDurationInterval]);

  const resetCallState = useCallback(() => {
    clearDurationInterval();
    activeCallIdRef.current = null;
    setIsMuted(false);
    setCallDuration(0);
    callStartTimeRef.current = null;
  }, [clearDurationInterval]);

  const connect = useCallback(async () => {
    if (sdkStatus !== SDK_STATUSES.DISCONNECTED || isConnectingRef.current) return;
    isConnectingRef.current = true;
    hasConnectionFailedRef.current = false;

    try {
      setError(null);
      setSdkStatus(SDK_STATUSES.CONNECTING);

      const tokenData = await generateWebrtcToken().unwrap();

      const client = new VonageClient();

      client.on("sessionError", (err) => {
        clientRef.current = null;
        setSdkStatus(SDK_STATUSES.DISCONNECTED);
        setError(err?.message || "Session error");
        hasConnectionFailedRef.current = true;
        // Clear any in-flight incoming invite so the modal doesn't
        // stay open on top of a dead SDK session. Accept/Decline
        // would silently no-op (they guard on clientRef.current), so
        // without this the user would be stuck staring at an
        // unresponsive modal.
        clearDurationInterval();
        activeCallIdRef.current = null;
        incomingCallIdRef.current = null;
        callWasAnsweredRef.current = false;
        setIncomingCallId(null);
        setIncomingCallerPhone(null);
        setIsMinimized(false);
        setCallState(CALL_STATES.IDLE);
      });

      client.on("callInvite", (callId, from) => {
        if (activeCallIdRef.current) {
          return;
        }
        setIncomingCallId(callId);
        incomingCallIdRef.current = callId;
        setIncomingCallerPhone(from || null);
        setCallState(CALL_STATES.INCOMING);
        setIsMinimized(false);
        setError(null);
      });

      client.on("callInviteCancel", (callId) => {
        // Only wipe the modal when the cancelled callId matches the one
        // we're currently ringing on. Vonage can fire cancel events for
        // older/retried invites while a fresh invite is still active --
        // blindly clearing state would make the modal disappear even
        // though the caller is still trying to get through.
        if (callId !== undefined && incomingCallIdRef.current !== callId) {
          return;
        }
        setIncomingCallId(null);
        incomingCallIdRef.current = null;
        setIncomingCallerPhone(null);
        setIsMinimized(false);
        if (!activeCallIdRef.current) {
          setCallState(CALL_STATES.IDLE);
        }
      });

      client.on("legStatusUpdate", (callId, legId, status) => {
        if (activeCallIdRef.current === callId && status === "ANSWERED") {
          callWasAnsweredRef.current = true;
          setCallState((prev) => {
            if (prev === CALL_STATES.CALLING) {
              startDurationTracking();
              return CALL_STATES.ACTIVE;
            }
            return prev;
          });
        }
      });

      // The BE handles comm history lifecycle via webhooks (answer webhook creates
      // the history, event webhooks set end time and call results). The FE only
      // manages UI state here.
      client.on("callHangup", (callId) => {
        if (activeCallIdRef.current === callId || incomingCallIdRef.current === callId) {
          clearDurationInterval();
          const wasAnswered = callWasAnsweredRef.current;
          setCallState(wasAnswered ? CALL_STATES.ENDED : CALL_STATES.FAILED);
          // Only the failed path should surface an error banner; a
          // clean hangup after an answered call must clear any stale
          // error from earlier in the session so the popover doesn't
          // flash "Call Failed" during the ENDED linger.
          if (wasAnswered) {
            setError(null);
          } else {
            setError("Call could not be connected");
          }
          activeCallIdRef.current = null;
          incomingCallIdRef.current = null;
          setIncomingCallerPhone(null);
          setIsMinimized(false);
          callWasAnsweredRef.current = false;
          if (endedToIdleTimerRef.current) clearTimeout(endedToIdleTimerRef.current);
          endedToIdleTimerRef.current = setTimeout(() => {
            endedToIdleTimerRef.current = null;
            resetCallState();
            setCallState(CALL_STATES.IDLE);
            setIsWidgetVisible(false);
            setError(null);
          }, 3000);
        }
      });

      await client.createSession(tokenData.token);
      setSdkStatus(SDK_STATUSES.CONNECTED);
      clientRef.current = client;
    } catch (err) {
      setError(err.message || "Connection failed");
      setSdkStatus(SDK_STATUSES.DISCONNECTED);
      hasConnectionFailedRef.current = true;
    } finally {
      isConnectingRef.current = false;
    }
  }, [
    sdkStatus,
    generateWebrtcToken,
    resetCallState,
    startDurationTracking,
    clearDurationInterval,
  ]);

  const disconnect = useCallback(async () => {
    if (endedToIdleTimerRef.current) {
      clearTimeout(endedToIdleTimerRef.current);
      endedToIdleTimerRef.current = null;
    }
    if (clientRef.current) {
      try {
        await clientRef.current.deleteSession();
      } catch {
        // ignore
      }
      clientRef.current = null;
    }
    resetCallState();
    setCallState(CALL_STATES.IDLE);
    setSdkStatus(SDK_STATUSES.DISCONNECTED);
    setError(null);
  }, [resetCallState]);

  const callPhone = useCallback(
    async ({ phoneNumber, debtorId, debtorContactPhoneId, e164Number }) => {
      if (!clientRef.current) {
        setError("Voice client not connected");
        return;
      }

      if (!fromPhoneNumber) {
        setError("Voice configuration not loaded");
        return;
      }

      if (endedToIdleTimerRef.current) {
        clearTimeout(endedToIdleTimerRef.current);
        endedToIdleTimerRef.current = null;
      }

      callWasAnsweredRef.current = false;
      setCallState(CALL_STATES.CALLING);
      setError(null);

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());

        const task = await createVoiceTask({
          debtorId,
          debtorContactPhoneId,
        }).unwrap();

        const serverCallPromise = clientRef.current.serverCall({
          to: e164Number || phoneNumber,
          from_number: fromPhoneNumber,
          task_id: task.id,
          user_uuid: currentUser?.uuid,
        });
        serverCallPromiseRef.current = serverCallPromise;
        const callId = await serverCallPromise;
        serverCallPromiseRef.current = null;
        activeCallIdRef.current = callId;
      } catch (err) {
        const errorDetails = err.data?.error?.details?.errors;
        const beMessage = errorDetails?.length
          ? errorDetails.join(". ")
          : err.data?.error?.user_message;
        setError(beMessage || err.message || "Call failed");
        setCallState(CALL_STATES.IDLE);
        resetCallState();
      }
    },
    [createVoiceTask, resetCallState, fromPhoneNumber, currentUser?.uuid],
  );

  const hangup = useCallback(async () => {
    if (!clientRef.current) return;

    if (serverCallPromiseRef.current) {
      try {
        const callId = await serverCallPromiseRef.current;
        await clientRef.current.hangup(callId);
      } catch {
        // serverCall failed or hangup failed -- callHangup event handles cleanup
      }
      serverCallPromiseRef.current = null;
      return;
    }

    if (!activeCallIdRef.current) return;
    try {
      await clientRef.current.hangup(activeCallIdRef.current);
    } catch {
      // callHangup event handles cleanup
    }
  }, []);

  const acceptIncoming = useCallback(
    async (callId) => {
      if (!clientRef.current) return;
      setError(null);
      try {
        await clientRef.current.answer(callId);
        // If the caller hung up while answer() was in-flight the
        // callHangup handler already fired, cleared the refs and
        // transitioned callState away from INCOMING. Don't revive
        // the call state or we'll leave the UI stuck in ACTIVE for
        // a call that no longer exists.
        if (incomingCallIdRef.current !== callId) return;
        callWasAnsweredRef.current = true;
        activeCallIdRef.current = callId;
        setIncomingCallId(null);
        incomingCallIdRef.current = null;
        setIncomingCallerPhone(null);
        setIsMinimized(false);
        setCallState(CALL_STATES.ACTIVE);
        startDurationTracking();
      } catch (err) {
        setError(err.message || "Failed to accept call");
        setCallState(CALL_STATES.IDLE);
        setIncomingCallId(null);
        incomingCallIdRef.current = null;
        setIncomingCallerPhone(null);
      }
    },
    [startDurationTracking],
  );

  const declineIncoming = useCallback(async (callId) => {
    if (!clientRef.current) return;
    try {
      await clientRef.current.reject(callId);
    } catch (err) {
      console.error("Failed to reject incoming call:", err);
    } finally {
      setIncomingCallId(null);
      incomingCallIdRef.current = null;
      setIncomingCallerPhone(null);
      setIsMinimized(false);
      setCallState(CALL_STATES.IDLE);
    }
  }, []);

  const toggleMute = useCallback(async () => {
    if (!clientRef.current || !activeCallIdRef.current) return;
    const newMuted = !isMuted;
    try {
      if (newMuted) {
        await clientRef.current.mute(activeCallIdRef.current);
      } else {
        await clientRef.current.unmute(activeCallIdRef.current);
      }
      setIsMuted(newMuted);
    } catch (err) {
      setError(err.message || "Failed to toggle mute");
    }
  }, [isMuted]);

  const showWidget = useCallback(() => setIsWidgetVisible(true), []);
  const hideWidget = useCallback(() => setIsWidgetVisible(false), []);

  const snoozeCall = useCallback(() => {
    setIsSnoozed(true);
    // Disconnect from Vonage entirely so inbound calls bypass this
    // browser and fall through to the connect-events webhook apology
    // path. Resuming triggers the auto-connect effect below.
    disconnect();
  }, [disconnect]);

  const resumeCall = useCallback(() => {
    // Clear the failure latch so the auto-connect effect retries on
    // resume (the user is explicitly opting back in).
    hasConnectionFailedRef.current = false;
    setIsSnoozed(false);
  }, []);

  // Auto-connect once all prerequisites are met: user loaded, call
  // permission granted, voice is configured and enabled for the
  // agency, the SDK is currently disconnected, and the user hasn't
  // snoozed. Without this effect, collectors would have to click the
  // floating widget before any inbound call could ring their browser
  // -- Vonage's connect action would find no Client SDK session and
  // the caller would hear the "no agent available" apology instead.
  //
  // The hasConnectionFailedRef guard prevents an infinite retry loop
  // when connect() fails: a failed connect resets sdkStatus to
  // DISCONNECTED, which would otherwise satisfy this effect's
  // dependencies and trigger another attempt immediately. The latch
  // is cleared by resumeCall() (the user explicitly retrying) or by a
  // fresh successful connect().
  useEffect(() => {
    if (
      currentUser &&
      isCallDebtorAuthorized &&
      isEnabled &&
      !isConfigLoading &&
      !isSnoozed &&
      !hasConnectionFailedRef.current &&
      sdkStatus === SDK_STATUSES.DISCONNECTED &&
      !isConnectingRef.current
    ) {
      connect();
    }
  }, [
    currentUser,
    isCallDebtorAuthorized,
    isEnabled,
    isConfigLoading,
    isSnoozed,
    sdkStatus,
    connect,
  ]);

  useEffect(() => {
    return () => {
      clearDurationInterval();
      if (endedToIdleTimerRef.current) {
        clearTimeout(endedToIdleTimerRef.current);
        endedToIdleTimerRef.current = null;
      }
      if (clientRef.current) {
        clientRef.current.deleteSession().catch(() => {});
        clientRef.current = null;
      }
    };
  }, [clearDurationInterval]);

  const minimizeIncomingModal = useCallback(() => setIsMinimized(true), []);
  const restoreIncomingModal = useCallback(() => setIsMinimized(false), []);

  const value = useMemo(
    () => ({
      isEnabled,
      isConfigLoading,
      sdkStatus,
      callState,
      incomingCallId,
      incomingCallerPhone,
      isMuted,
      callDuration,
      error,
      isWidgetVisible,
      isMinimized,
      isSnoozed,
      connect,
      disconnect,
      callPhone,
      hangup,
      acceptIncoming,
      declineIncoming,
      toggleMute,
      showWidget,
      hideWidget,
      minimizeIncomingModal,
      restoreIncomingModal,
      snoozeCall,
      resumeCall,
    }),
    [
      isEnabled,
      isConfigLoading,
      sdkStatus,
      callState,
      incomingCallId,
      incomingCallerPhone,
      isMuted,
      callDuration,
      error,
      isWidgetVisible,
      isMinimized,
      isSnoozed,
      connect,
      disconnect,
      callPhone,
      hangup,
      acceptIncoming,
      declineIncoming,
      toggleMute,
      showWidget,
      hideWidget,
      minimizeIncomingModal,
      restoreIncomingModal,
      snoozeCall,
      resumeCall,
    ],
  );

  return <InternalDialerContext.Provider value={value}>{children}</InternalDialerContext.Provider>;
}
